import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  User as UserIcon, LogOut, ChevronDown, Globe, Briefcase, Gift, Users, DollarSign,
  Menu, X, ChevronRight, Ticket, Compass, MessageSquare, Building2,
  Sparkles, LifeBuoy, LayoutDashboard,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import NotificationBell from '@/components/NotificationBell';

const PRODUCT_LINKS = [
  { label: 'Creator Hubs', desc: 'Your branded home & link-in-bio', href: '/#hubs', icon: Globe },
  { label: 'Ticketing', desc: 'Tiers, add-ons & revenue splits', href: '/#ticketing', icon: Ticket },
  { label: 'Community', desc: 'Tier-locked feeds & member perks', href: '/#community', icon: Users },
  { label: 'Monetization', desc: 'Subscriptions & ambassadors', href: '/#revenue', icon: DollarSign },
];

const SOLUTION_LINKS = [
  { label: 'For Organisers', desc: 'DJs, promoters & collectives', href: '/#for-organisers', icon: Sparkles },
  { label: 'For Venues', desc: 'Private hire & reservations', href: '/#for-venues', icon: Building2 },
  { label: 'How it works', desc: 'Live in under five minutes', href: '/#how-it-works', icon: Compass },
];

const RESOURCE_LINKS = [
  { label: 'Guides', desc: 'Playbooks & city guides', href: '/guides', icon: Compass, internal: true },
  { label: 'Product Feedback', desc: 'Shape the roadmap', href: '/feedback', icon: MessageSquare, internal: true },
  { label: 'Become a Partner', desc: 'List your venue', href: '/partner/register', icon: Briefcase, internal: true },
];

const NavDropdown = ({ label, items, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className="flex items-center gap-1 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
        data-testid={testId}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-72 bg-[#0f1117] border-white/10 p-1.5">
      {items.map((item) => (
        <DropdownMenuItem key={item.label} asChild className="focus:bg-white/[0.06] rounded-lg p-0">
          {item.internal ? (
            <Link to={item.href} className="flex items-start gap-3 w-full px-3 py-2.5" data-testid={`nav-item-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <item.icon className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-zinc-100">{item.label}</span>
                <span className="block text-[11px] text-zinc-500">{item.desc}</span>
              </span>
            </Link>
          ) : (
            <a href={item.href} className="flex items-start gap-3 w-full px-3 py-2.5" data-testid={`nav-item-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <item.icon className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-zinc-100">{item.label}</span>
                <span className="block text-[11px] text-zinc-500">{item.desc}</span>
              </span>
            </a>
          )}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const Navigation = () => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { isAuthenticated, isPartner, user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [routerLocation.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const isAdmin = user?.role === 'admin';
  const userLinks = isAuthenticated ? [
    { label: isPartner ? 'Partner Dashboard' : 'My Dashboard', href: isPartner ? '/partner/dashboard' : '/dashboard', icon: LayoutDashboard },
    { label: 'Creator Hub', href: '/my-hubs', icon: Globe },
    ...(!isPartner ? [{ label: 'My Experiences', href: '/my-experiences', icon: Gift }] : []),
    ...(isAdmin ? [{ label: 'Admin Console', href: '/admin', icon: Briefcase }] : []),
  ] : [];

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full bg-[#08090d]/85 backdrop-blur-xl border-b border-white/[0.08]"
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo + primary nav */}
            <div className="flex items-center gap-10">
              <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
                <span className="text-lg lg:text-xl font-bold tracking-wider text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                  EVENT<span className="text-indigo-500">★</span>GOERS
                </span>
              </Link>

              <nav className="hidden lg:flex items-center gap-7">
                <NavDropdown label="Product" items={PRODUCT_LINKS} testId="nav-product" />
                <NavDropdown label="Solutions" items={SOLUTION_LINKS} testId="nav-solutions" />
                <Link to="/creators" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors" data-testid="nav-communities">Communities</Link>
                <Link to="/pricing" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</Link>
                <NavDropdown label="Resources" items={RESOURCE_LINKS} testId="nav-resources" />
              </nav>
            </div>

            {/* Right: account actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              {isAuthenticated && <NotificationBell />}

              <div className="hidden lg:flex items-center gap-3">
                {isAuthenticated ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2" data-testid="nav-user-menu">
                        <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-semibold">
                          {user?.full_name?.charAt(0) || 'U'}
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-[#0f1117] border-white/10">
                      {userLinks.map((link) => (
                        <DropdownMenuItem key={link.href} className="text-zinc-200 focus:bg-white/[0.06]" onClick={() => navigate(link.href)}>
                          <link.icon className="w-4 h-4 mr-2 text-zinc-400" />
                          {link.label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem className="text-zinc-200 focus:bg-white/[0.06]" onClick={handleLogout} data-testid="nav-logout">
                        <LogOut className="w-4 h-4 mr-2 text-zinc-400" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <button
                      className="text-sm font-medium text-zinc-400 hover:text-white transition-colors"
                      onClick={() => navigate('/auth')}
                      data-testid="nav-signin-btn"
                    >
                      Sign in
                    </button>
                    <Button
                      className="h-10 px-5 rounded-full bg-white text-[#08090d] hover:bg-zinc-200 font-semibold active:scale-95 transition-transform"
                      onClick={() => navigate('/my-hubs')}
                      data-testid="nav-start-free-btn"
                    >
                      Start free
                    </Button>
                  </>
                )}
              </div>

              <button
                className="lg:hidden p-2 text-zinc-300 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 220 }}
              className="fixed top-0 right-0 bottom-0 w-[88%] max-w-sm bg-[#08090d] border-l border-white/10 z-50 lg:hidden overflow-y-auto"
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold tracking-wider text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                  EVENT<span className="text-indigo-500">★</span>GOERS
                </Link>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-zinc-400 hover:text-white rounded-lg" data-testid="mobile-drawer-close">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {isAuthenticated ? (
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                      {user?.full_name?.charAt(0) || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-zinc-100 truncate">{user?.full_name || 'User'}</p>
                      <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                  {userLinks.map((link) => (
                    <Link
                      key={link.href} to={link.href} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between p-3 rounded-lg text-zinc-300 hover:bg-white/[0.05] transition-colors"
                    >
                      <span className="flex items-center gap-3"><link.icon className="w-5 h-5 text-zinc-500" />{link.label}</span>
                      <ChevronRight className="w-4 h-4 text-zinc-600" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-4 border-b border-white/10 space-y-3">
                  <Button
                    className="w-full h-11 rounded-full bg-white text-[#08090d] hover:bg-zinc-200 font-semibold"
                    onClick={() => { navigate('/my-hubs'); setMobileMenuOpen(false); }}
                    data-testid="mobile-start-free-btn"
                  >
                    Start free
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-11 rounded-full border-white/15 bg-transparent text-zinc-200 hover:bg-white/[0.06]"
                    onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                    data-testid="mobile-signin-btn"
                  >
                    Sign in
                  </Button>
                </div>
              )}

              <div className="p-4">
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-[0.18em] mb-3">Product</p>
                <div className="space-y-1 mb-6">
                  {PRODUCT_LINKS.map((link) => (
                    <a
                      key={link.label} href={link.href} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-lg text-zinc-300 hover:bg-white/[0.05] transition-colors"
                    >
                      <link.icon className="w-5 h-5 text-indigo-400" />
                      <span className="font-medium text-sm">{link.label}</span>
                    </a>
                  ))}
                </div>

                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-[0.18em] mb-3">Solutions</p>
                <div className="space-y-1 mb-6">
                  {SOLUTION_LINKS.map((link) => (
                    <a
                      key={link.label} href={link.href} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-lg text-zinc-300 hover:bg-white/[0.05] transition-colors"
                    >
                      <link.icon className="w-5 h-5 text-indigo-400" />
                      <span className="font-medium text-sm">{link.label}</span>
                    </a>
                  ))}
                </div>

                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-[0.18em] mb-3">Explore</p>
                <div className="space-y-1">
                  <Link to="/creators" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-lg text-zinc-300 hover:bg-white/[0.05] transition-colors" data-testid="mobile-nav-communities">
                    <Users className="w-5 h-5 text-indigo-400" /><span className="font-medium text-sm">Communities</span>
                  </Link>
                  <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-lg text-zinc-300 hover:bg-white/[0.05] transition-colors" data-testid="mobile-nav-pricing">
                    <DollarSign className="w-5 h-5 text-indigo-400" /><span className="font-medium text-sm">Pricing</span>
                  </Link>
                  {RESOURCE_LINKS.map((link) => (
                    <Link
                      key={link.label} to={link.href} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-lg text-zinc-300 hover:bg-white/[0.05] transition-colors"
                    >
                      <link.icon className="w-5 h-5 text-indigo-400" />
                      <span className="font-medium text-sm">{link.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="p-4 border-t border-white/10">
                <button className="flex items-center gap-3 w-full p-3 rounded-lg text-zinc-400 hover:bg-white/[0.05] transition-colors">
                  <LifeBuoy className="w-5 h-5" /><span className="text-sm">Help &amp; Support</span>
                </button>
                {isAuthenticated && (
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full p-3 rounded-lg text-rose-400 hover:bg-rose-500/10 transition-colors"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-5 h-5" /><span className="text-sm">Sign out</span>
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
